<template>
    <div>
        <div class="assigned mb-4 px-2" v-if="localAssigned.length">
            <div
                class="bg-gray-100 border-radius py-2 py-lg-0 px-2 mb-1 row"
                v-for="(relative, key) in localAssigned"
                :key="`assigned-${key}`">

                <div class="col-11 col-lg-4 col-xl-6 align-items-center d-flex">
                    <user-quick-view :user-id="relative.id" :text="relative.label"/>
                </div>

                <div class="col-1 order-1 order-lg-3 d-flex justify-content-end" v-if="!disabled">
                    <i class="fi fi-rr-cross" @click="removeRelative(key)" role="button"></i>
                </div>

                <!-- Relation -->
                <div class="col-12 col-lg-3 col-xl-2 py-2 py-lg-0 order-2 order-lg-1">
                    <k-select
                        :disabled="disabled"
                        :key="`relation-${relative.id}`"
                        :placeholder="trans('general.page.children.fields.select_relation')"
                        v-model="localAssigned[key].relation"
                        :options="relationOptions"
                        :required="true"
                        :show-selected-options="false"
                        :allow-search="false"
                        :simplified="true"
                        auto-close="true"/>
                </div>

                <!-- Emergency Contact -->
                <div class="col-12 col-lg-4 col-xl-3 d-flex align-items-center order-3 order-lg-2">
                    <basic-input
                        :disabled="disabled || localAssigned.length === 1"
                        :key="`emergency-${relative.id}`"
                        :id="'relativeAssignEmergencyContact' + relative.id"
                        type="switch"
                        response="number"
                        :label="trans('general.page.children.fields.emergency_contact')"
                        v-model="localAssigned[key].is_emergency_contact" />
                </div>
            </div>
        </div>
        <div v-else class="border-radius bg-gray-100 p-3 mb-3">
            <p class="text-center mb-0 small title">
                {{ trans('general.page.children.no_relatives_assigned') }}
            </p>
        </div>

        <div class="assign" v-if="!disabled">
            <div class="addNew border border-radius p-3" v-if="addNew">
                <div class="d-flex justify-content-between">
                    <label class="large">{{ trans('general.page.children.add_new_relative') }}</label>
                    <i class="fi fi-rr-cross" @click="addNew = false" role="button"></i>
                </div>
                <div class="row">
                    <!-- Name -->
                    <div class="col-12 col-lg-4">
                        <basic-input
                            type="text"
                            :required="true"
                            :label="trans('general.page.children.fields.name')"
                            v-model="form.name"
                            :error="errorFields.name"
                            @reset-error="errorFields.name = null" />
                    </div>

                    <!-- Surname -->
                    <div class="col-12 col-lg-4">
                        <basic-input
                            type="text"
                            :required="true"
                            :label="trans('general.page.children.fields.surname')"
                            v-model="form.surname"
                            :error="errorFields.surname"
                            @reset-error="errorFields.surname = null" />
                    </div>

                    <!-- Relation -->
                    <div class="col-12 col-lg-4">
                        <k-select
                            :placeholder="trans('general.page.children.fields.select_relation')"
                            :label="trans('general.page.children.fields.relation.relation')"
                            v-model="form.relation"
                            :options="relationOptions"
                            :required="true"
                            :show-selected-options="false"
                            :allow-search="false"
                            auto-close="true" />
                    </div>

                    <!-- Email -->
                    <div class="col-12 col-lg-4">
                        <basic-input
                            type="email"
                            :required="true"
                            :label="trans('general.page.profile.fields.email')"
                            v-model="form.email"
                            :error="errorFields.email"
                            @reset-error="errorFields.email = null" />
                    </div>

                    <!-- Phone -->
                    <div class="col-12 col-lg-4">
                        <basic-input
                            type="text"
                            :required="true"
                            :label="trans('general.page.profile.fields.phone')"
                            v-model="form.phone"
                            :error="errorFields.phone"
                            @reset-error="errorFields.phone = null" />
                    </div>

                    <!-- Emergency Contact -->
                    <div class="col-12 col-lg-4 d-flex align-items-center">
                        <basic-input
                            type="switch"
                            response="number"
                            :label="trans('general.page.children.fields.emergency_contact')"
                            v-model="form.is_emergency_contact" />
                    </div>

                    <!-- Submit -->
                    <div class="d-flex justify-content-end align-items-center col-12 gap-2 mt-4">
                        <basic-button
                            cls="btn-success"
                            :text="trans('general.page.children.create_and_assign')"
                            :loading="submitting"
                            :disabled="submitting"
                            @click="submitRelative" />
                    </div>
                </div>
            </div>

            <div class="select" v-else>
                <k-select
                    :label="trans('general.children.assign_relatives')"
                    v-model="localAssigned"
                    :options="relativeOptions"
                    :searchable="true"
                    :server-side-search="true"
                    :multiple="true"
                    :show-selected-options="false"
                    @search="loadRelatives">
                    <template v-slot:header>
                        <div class="dropdown-item title d-flex align-items-center gap-2" role="button"
                             @click="addNew = true">
                            <i class="fi fi-rr-plus"></i>
                            {{ trans('general.defaults.add_new') }}
                        </div>
                    </template>
                </k-select>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import KSelect from "../../../theme/molecules/KSelect.vue";
import BasicInput from "../../../theme/atoms/BasicInput.vue";
import BasicButton from "../../../theme/atoms/BasicButton.vue";
import { ChildCloseRelativesFields } from "../services/ChildCloseRelatives";
import UserQuickView from "../../Users/organisms/UserQuickView.vue";

export default {
    name: "AssignRelatives",
    components: {UserQuickView, BasicInput, KSelect, BasicButton },
    props: {
        value: {
            type: Array,
            required: false,
            default: () => [],
        },
        profile: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            submitting: false,
            localAssigned: [],
            relativeOptions: [],
            addNew: false,
            initialRelation: [
                {
                    id: ChildCloseRelativesFields[0],
                    label: trans(`general.page.children.fields.relation.${ChildCloseRelativesFields[0]}`)
                },
            ],
            form: {
                name: null,
                surname: null,
                relation: null,
                email: null,
                phone: null,
                is_emergency_contact: 1,
            },
            errors: [],
            errorFields: {
                name: null,
                surname: null,
                email: null,
                phone: null,
            },
        };
    },
    computed: {
        relationOptions() {
            return ChildCloseRelativesFields.map(a => ({
                id: a,
                label: trans('general.page.children.fields.relation.' + a),
            }));
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                this.localAssigned = val;
            },
        },
        localAssigned: {
            handler(val) {
                if(val.length === 1) {
                    this.localAssigned[0].is_emergency_contact = 1;
                } else if(val.length > 1) {
                    const noEmergencyContact = val.every(item => !item.is_emergency_contact);
                    if(noEmergencyContact) {
                        this.localAssigned[0].is_emergency_contact = 1;
                    }
                }

                this.$emit('input', val);
            },
            deep: true,
        },
        addNew(val) {
            this.$emit('add-new', val);
        },
    },
    methods: {
        loadRelatives(search = '') {
            axios.get(`/parents`, { params: { search, limit: 10, include: 'children' } })
                .then(response => {
                    this.relativeOptions = response.data.data.data.map(a => ({
                        id: a.id,
                        label: `${a.name} ${a.surname}`,
                        type: 'existing',
                        relation: [
                            {
                                id: this.relationOptions[0].id,
                                label: trans('general.page.children.fields.relation.' + this.relationOptions[0].id)
                            }
                        ],
                        is_emergency_contact: 0,
                    }));
                });
        },
        submitRelative() {
            this.submitting = true;
            this.errors = [];

            this.validateData();

            if (this.errors.length) {
                this.submitting = false;
                return;
            }

            const newRelative = {
                label: `${this.form.name} ${this.form.surname}`,
                id: this.form.email,
                type: 'new',
                data: { ...this.form },
                is_emergency_contact: this.form.is_emergency_contact,
                relation: this.form.relation,
            };

            this.localAssigned.push(newRelative);
            this.relativeOptions.push(newRelative);

            this.resetForm();
        },
        validateData() {
            Object.keys(this.errorFields).forEach(key => {
                if (!this.form[key]) {
                    this.errorFields[key] = trans('validation.required', { attribute: trans('general.page.children.fields.' + key) });
                    this.errors.push(key);
                }
            });

            if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
                this.errorFields.email = trans('validation.email', { attribute: trans('general.page.profile.fields.email') });
                this.errors.push('email');
            }
        },
        resetForm() {
            this.form = {
                name: null,
                surname: null,
                birth_date: null,
                group_id: null,
            };
            this.addNew = false;
            this.submitting = false;
            this.errors = [];
        },
        removeRelative(index) {
            this.localAssigned.splice(index, 1);
        },
    },
    mounted() {
        this.form.relation = this.initialRelation;
        this.loadRelatives();
    },
};
</script>

<style scoped>
.addNew .group {
    margin-top: 10px;
}
</style>
